/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 * Inquiry close modal container component
 *
 * @author Julia Ignatieva
 * @author Ilya Babanov
 * @author Alexander Golshtein
 * @version 1.25.0
 * @since 1.15.0
 */

import { Fragment, useCallback } from 'react';

import { Formik, FormikProps } from 'formik';

import { LinkBlock } from '@components/common';
import FormFieldTextarea from '@modules/form/components/FormFieldTextarea';
import { ICommonTranslation } from '@modules/internationalization/types';
import { IPageInquiryInfoTranslation } from '@modules/internationalization/types/pages/inquiryInfo';
import { Loader } from '@modules/loader/components/Loader';
import { useModalOpenError } from '@modules/modal/hooks/useModalOpenError';
import { IModalRenderProps } from '@modules/modal/types/modalTypes';
import ButtonServiceUserDenied from '@modules/role/components/ButtonServiceUserDenied';

import { useLazyGetInquiryInfoGeneralQuery, usePostInquiryCloseMutation } from '../../store';
import { InquiryInfoNoteData } from '../../types/inquiryInfoType';
import { CloseInquiryFooterStyled, CloseInquiryNoteStyled } from './styled/InquiryCloseStyled';

export interface IInquiryInfoCloseModal extends Pick<IModalRenderProps, 'onCloseModal'> {
    inquiryId: string;
    translation: IPageInquiryInfoTranslation;
    translationCommon: ICommonTranslation;
}

const initialValues: InquiryInfoNoteData = {
    note: '',
};

const InquiryCloseModalForm = (props: IInquiryInfoCloseModal) => {

    const {
        inquiryId,
        translation,
        translationCommon,

        onCloseModal,
    } = props;

    const { open: openErrorModal } = useModalOpenError();
    const [getInquiryInfoGeneral] = useLazyGetInquiryInfoGeneralQuery();
    const [postInquiryClose, { isLoading: isModalLoading }] = usePostInquiryCloseMutation();

    const closeInquiryHandler = useCallback((note: InquiryInfoNoteData) => {
        postInquiryClose({ inquiryId, note })
            .unwrap()
            .then(() => {
                onCloseModal();
                getInquiryInfoGeneral({ inquiryId });
            })
            .catch(() => {
                onCloseModal();
                openErrorModal('', translation.closeModal.errorText)
            });
    }, [
        getInquiryInfoGeneral,
        inquiryId,
        onCloseModal,
        openErrorModal,
        postInquiryClose,
        translation.closeModal.errorText,
    ]);

    return (
        <Fragment>
            <Formik
                initialValues={initialValues}
                onSubmit={closeInquiryHandler}
            >
                {
                    ({
                        handleSubmit,
                        isValid,
                    }: FormikProps<InquiryInfoNoteData>) => (
                        <form onSubmit={handleSubmit}>
                            <CloseInquiryNoteStyled>
                                <FormFieldTextarea
                                    dataName="inquiry-close-modal-comment"
                                    label={translation.closeModal.comment}
                                    name="note"
                                    rows={4}
                                />
                            </CloseInquiryNoteStyled>
                            <CloseInquiryFooterStyled>
                                <ButtonServiceUserDenied
                                    disabled={!isValid}
                                    kind="medium"
                                    type="submit"
                                >
                                    {translation.closeModal.btn}
                                </ButtonServiceUserDenied>
                                <LinkBlock
                                    className="close-inquiry__footer-link"
                                    onClick={onCloseModal}
                                >
                                    {translationCommon.modal.cancel}
                                </LinkBlock>
                            </CloseInquiryFooterStyled>
                        </form>
                    )
                }
            </Formik>
            {
                isModalLoading &&
                <Loader showLoader={isModalLoading} />
            }
        </Fragment>
    );
};

export default InquiryCloseModalForm;
